import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import waypointBg from '@ibk/shared-assets/icons/waypoint-background.svg';
import loginBg from '@ibk/shared-assets/images/hero-login-dominioescritorio.png';
import { getSelectedAccountInfo, saveAccountId, saveAllAccounts, usePersonAccess } from '@ibk/shared-auth';
import { selectIsAnyProductLoading, setSelectedAccountId, useAppDispatch, useAppSelector, useGetProductRelationShips, useHomeProducts, } from '@ibk/shared-config';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { ignoredRoutes, useGetHomePermissions } from '@ibk/shared-permissioning';
import { TranslationProvider, useTranslation } from '@ibk/shared-translation';
import { ApiProductStatus, FeatureFlagUseCase, ProductAlias } from '@ibk/shared-types';
import { Loading, LoadingScreen, CompanySelection as SharedCompanySelection, useCnpjAlphanumeric, } from '@ibk/shared-ui';
import { getAccountIdentifierFormatted, getAccountName, getLastAccessedCompany, saveLastAccessedCompany, sortCompaniesByLastAccessed, } from '@ibk/shared-utils';
import { dominioEscritorioTranslations } from '../../translations';
import { useContabilCompanyLinkRedirect } from '../ContabilCompanyLink/hooks/useContabilCompanyLinkRedirect';
const HOME_ROUTE = '/home';
const ROUTE_BY_PERMISSION = [
    ['hasAccountPj', '/conta-pj'],
    ['hasDigitalAccounting', '/contabilidade-digital/contas-conectadas'],
    ['hasBenefit', '/beneficios'],
    ['hasBilling', '/cobrancas'],
];
const resolveRouteByPermissions = (permissions, hasTwoOrMoreAccesses) => {
    if (hasTwoOrMoreAccesses) {
        return HOME_ROUTE;
    }
    const matchedRoute = ROUTE_BY_PERMISSION.find(([permission]) => permissions[permission]);
    return matchedRoute ? matchedRoute[1] : HOME_ROUTE;
};
const CompanySelectionContent = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const { t } = useTranslation();
    const { data, loading, error, refetch } = usePersonAccess();
    const { saveBillingProduct } = useGetProductRelationShips();
    const { getBillingProduct } = useGetProductRelationShips();
    const hasProductsLoading = useAppSelector(selectIsAnyProductLoading);
    const [fetchingValidations, setFetchingValidations] = useState(false);
    const lastAccessedCompanyId = getLastAccessedCompany();
    const { finishedValidations, hasAccountPjPermission, hasBenefitPermission, hasBillingPermission, hasDigitalAccountingPermission, } = useGetHomePermissions();
    const { products, isLoading } = useHomeProducts({
        enabled: !!fetchingValidations,
    });
    const { isFeatureFlagWhiteListed, getFeatureFlags, clearCache } = useFeatureFlag(undefined, {
        autoLoad: !ignoredRoutes.includes(location.pathname),
    });
    const { isAlphanumericEnabled, maskCnpj: maskCnpjAlpha, isValidCnpj: isValidCnpjAlpha } = useCnpjAlphanumeric();
    const { resolveDestination } = useContabilCompanyLinkRedirect();
    const hasZestraFlag = isFeatureFlagWhiteListed(FeatureFlagUseCase.NEW_IBKS_ESCRITORIO_MODULE_BILLING);
    const getUserAccesses = () => {
        const storedProduct = getBillingProduct();
        const accountantBillingProduct = products[ProductAlias.COBRANCAS];
        const isAccountantBillingContracted = !accountantBillingProduct.isLoading && accountantBillingProduct.data?.status === ApiProductStatus.ACTIVE;
        const accountantContabilidadeDigitalProduct = products[ProductAlias.CONTABILIDADE_DIGITAL];
        const isAccountantContabilidadeDigitalActive = !accountantContabilidadeDigitalProduct.isLoading &&
            accountantContabilidadeDigitalProduct.data?.status === ApiProductStatus.ACTIVE;
        const permissions = {
            hasAccountPj: !!hasAccountPjPermission,
            hasDigitalAccounting: !!hasDigitalAccountingPermission && isAccountantContabilidadeDigitalActive,
            hasBenefit: !!hasBenefitPermission,
            hasBilling: !!hasZestraFlag && !!hasBillingPermission && isAccountantBillingContracted && !!storedProduct,
        };
        const permissionValues = Object.values(permissions).filter(Boolean);
        const hasTwoOrMoreAccesses = !permissionValues.length || permissionValues.length >= 2;
        return { permissions, hasTwoOrMoreAccesses };
    };
    useEffect(() => {
        refetch();
        return () => {
            setFetchingValidations(false);
        };
    }, []);
    useEffect(() => {
        const runValidationNav = async () => {
            if (fetchingValidations && finishedValidations && !hasProductsLoading && !isLoading) {
                const { permissions, hasTwoOrMoreAccesses } = getUserAccesses();
                const targetRoute = resolveRouteByPermissions(permissions, hasTwoOrMoreAccesses);
                navigate(targetRoute === HOME_ROUTE ? await resolveDestination(getSelectedAccountInfo()?.entityId) : targetRoute);
            }
        };
        const delayDebounceFn = setTimeout(() => {
            void runValidationNav();
        }, 600);
        return () => clearTimeout(delayDebounceFn);
    }, [fetchingValidations, finishedValidations, hasProductsLoading, isLoading, navigate, resolveDestination]);
    const companies = React.useMemo(() => {
        if (!data || !Array.isArray(data))
            return [];
        const mappedCompanies = data.map(item => {
            const documentNumber = item.entity.documentNumber;
            const cnpj = isAlphanumericEnabled && isValidCnpjAlpha(documentNumber)
                ? maskCnpjAlpha(documentNumber)
                : getAccountIdentifierFormatted(documentNumber);
            return {
                cnpj,
                name: getAccountName(item),
                id: item.targetId,
            };
        });
        return sortCompaniesByLastAccessed(mappedCompanies, lastAccessedCompanyId);
    }, [data, lastAccessedCompanyId, isAlphanumericEnabled, isValidCnpjAlpha, maskCnpjAlpha]);
    const handleSelectCompanyService = async (companyId) => {
        clearCache();
        dispatch(setSelectedAccountId(companyId));
        if (data) {
            saveAllAccounts(data);
            saveAccountId(data, companyId);
            await saveBillingProduct(companyId);
        }
        saveLastAccessedCompany(companyId);
        const flagsResult = await getFeatureFlags(true);
        if (!flagsResult.featureFlags?.length) {
            clearCache();
        }
        setFetchingValidations(companyId);
    };
    const handleGoBackService = () => {
        const cameFromAuthRoutesFlow = ['two-factor-verify', 'auth-flow'];
        const cameFromAuthFlow = cameFromAuthRoutesFlow.includes(location.state?.from);
        if (cameFromAuthFlow) {
            navigate('/login', { replace: true });
        }
        else if (window.history.length > 1) {
            navigate(-1);
        }
        else {
            navigate('/login');
        }
    };
    if (fetchingValidations) {
        return _jsx(Loading, { fullScreen: true, "data-testid": 'company-selection-loading-permissions' });
    }
    if (loading) {
        return _jsx(LoadingScreen, { "data-testid": 'company-selection-loading-office' });
    }
    if (error) {
        return (_jsxs("div", { style: {
                height: '100vh',
                flexDirection: 'column',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }, "data-testid": 'company-selection-error-office', children: [_jsxs("div", { "data-testid": 'company-selection-error-message-office', style: { color: 'red', marginBottom: '1rem' }, children: ["Erro: ", error] }), _jsx("button", { "data-testid": 'company-selection-retry-button-office', onClick: () => window.location.reload(), children: "Tentar novamente" })] }));
    }
    return (_jsx(SharedCompanySelection, { "data-testid": 'accountant-company-selection-office', loginBg: loginBg, companies: companies, logoType: 'servicosdigitais', waypointBg: waypointBg, onGoBack: handleGoBackService, onSelectCompany: handleSelectCompanyService, title: t('company-selection.title'), backButtonText: t('company-selection.back-button'), submitButtonText: t('company-selection.submit-button'), initialCompanyId: lastAccessedCompanyId || undefined }));
};
export default function CompanySelection() {
    return (_jsx(TranslationProvider, { translations: dominioEscritorioTranslations, defaultLanguage: 'pt-BR', children: _jsx(CompanySelectionContent, {}) }));
}
